import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  ListItemButton,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import {
  Shield as ShieldIcon,
  Help as HelpIcon,
  Info as InfoIcon,
  Logout as LogoutIcon,
  PrivacyTip as PrivacyTipIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useCompany } from '../hooks/useAuth';
import { AuthService } from '../services/authService';
import { AppColors } from '../theme/colors';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { ResponsiveUserLayout } from '../components/ResponsiveUserLayout';
import { ResponsiveAdminLayout } from '../components/ResponsiveAdminLayout';

export const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { company } = useCompany(user?.uid || null);
  const isDesktop = useIsDesktop();
  
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [anonymityDialogOpen, setAnonymityDialogOpen] = useState(false);

  /**
   * ログアウト後の遷移先は、入り方に合わせる。
   *
   * 管理者が管理画面からログアウトしたのに投稿者用の画面が出るのは自然でなく、
   * 「管理者でログイン」を押し直すことになる。
   * 投稿者は会社IDもパスワードも知らないため、QRの案内画面へ送る。
   */
  const handleLogout = async () => {
    const isAdmin = user?.userType === 'admin' || user?.userType === 'superadmin';

    try {
      await AuthService.signOut();
      navigate(isAdmin ? '/admin-login' : '/scan-required');
    } catch (error) {
      console.error('ログアウトエラー:', error);
    }
  };

  const handleAnonymityInfo = () => {
    setAnonymityDialogOpen(true);
  };

  const isAdmin = user?.userType === 'admin' || user?.userType === 'superadmin';

  // 管理者かつPC表示の場合のみ管理画面レイアウトを使用（スマホ表示はユーザー用レイアウト）
  if (isAdmin && isDesktop) {
    return (
      <ResponsiveAdminLayout pageTitle="設定">
        <Box>
          {/* モバイル専用ヘッダー（戻る＋設定） */}
          {!isDesktop && (
            <AppBar position="static" sx={{ bgcolor: 'white', boxShadow: 'none', borderBottom: '1px solid #e0e0e0' }}>
              <Toolbar>
                <IconButton edge="start" onClick={() => navigate(-1)} sx={{ color: 'black' }}>
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" sx={{ flexGrow: 1, color: 'black', fontWeight: 600 }}>設定</Typography>
              </Toolbar>
            </AppBar>
          )}

          {/* 基準の余白の状態: PCは最大幅1000px・中央寄せ */}
          <Box sx={{ 
            flex: 1,
            px: 2,
            pt: 2,
            pb: 0,
            maxWidth: isDesktop ? '1000px' : '100%',
            mx: isDesktop ? 'auto' : 0,
            width: '100%',
          }}>
              {/* ユーザー情報セクション */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: AppColors.primary,
                    mb: 1,
                    px: 2,
                  }}
                >
                  ユーザー情報
                </Typography>
                <Card sx={{ boxShadow: 2, mx: 2 }}>
                  <List sx={{ p: 0 }}>
                    <ListItem>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: AppColors.primary }}>
                          <Typography sx={{ fontSize: 16, fontWeight: 'bold', color: 'white' }}>
                            {(user?.displayName || user?.email || 'U').charAt(0)}
                          </Typography>
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
                              {user?.displayName || 'ユーザー'}
                            </Typography>
                            {(user?.userType === 'superadmin' || user?.userType === 'admin') && (
                              <Chip
                                label={user?.userType === 'superadmin' ? 'システム管理者' : '管理者'}
                                size="small"
                                sx={{ height: 22, bgcolor: `${AppColors.primary}33`, color: AppColors.primary, fontWeight: 600 }}
                              />
                            )}
                          </Box>
                        }
                        secondary={user?.email || 'メール未設定'}
                        secondaryTypographyProps={{
                          fontSize: 14,
                          color: AppColors.textSecondary,
                        }}
                      />
                    </ListItem>
                  </List>
                </Card>
              </Box>

              {/* プライバシーセクション（管理者・システム管理者には表示しない） */}
              {!isAdmin && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 600,
                      color: AppColors.primary,
                      mb: 1,
                      px: 2,
                    }}
                  >
                    プライバシー
                  </Typography>
                  <Card sx={{ boxShadow: 2, mx: 2 }}>
                    <List sx={{ p: 0 }}>
                      <ListItemButton
                        onClick={handleAnonymityInfo}
                        sx={{
                          '&:hover': { bgcolor: '#f5f5f5' },
                        }}
                      >
                        <ListItemIcon>
                          <ShieldIcon sx={{ color: AppColors.primary }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="匿名性について"
                          secondary="あなたの投稿は完全に匿名です"
                          primaryTypographyProps={{
                            fontSize: 16,
                            fontWeight: 500,
                          }}
                          secondaryTypographyProps={{
                            fontSize: 14,
                            color: AppColors.textSecondary,
                          }}
                        />
                      </ListItemButton>
                    </List>
                  </Card>
                </Box>
              )}

              {/* ヘルプセクション */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: AppColors.primary,
                    mb: 1,
                    px: 2,
                  }}
                >
                  ヘルプ
                </Typography>
                <Card sx={{ boxShadow: 2, mx: 2 }}>
                  <List sx={{ p: 0 }}>
                    <ListItemButton
                      onClick={() => navigate('/help')}
                      sx={{
                        '&:hover': { bgcolor: '#f5f5f5' },
                      }}
                    >
                      <ListItemIcon>
                        <HelpIcon sx={{ color: AppColors.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="ヘルプ"
                        secondary="よくある質問とサポート"
                        primaryTypographyProps={{
                          fontSize: 16,
                          fontWeight: 500,
                        }}
                        secondaryTypographyProps={{
                          fontSize: 14,
                          color: AppColors.textSecondary,
                        }}
                      />
                    </ListItemButton>
                    <ListItemButton
                      onClick={() => navigate('/about-app')}
                      sx={{
                        '&:hover': { bgcolor: '#f5f5f5' },
                      }}
                    >
                      <ListItemIcon>
                        <InfoIcon sx={{ color: AppColors.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="アプリについて"
                        secondary="アプリの機能と使い方"
                        primaryTypographyProps={{
                          fontSize: 16,
                          fontWeight: 500,
                        }}
                        secondaryTypographyProps={{
                          fontSize: 14,
                          color: AppColors.textSecondary,
                        }}
                      />
                    </ListItemButton>
                  </List>
                </Card>
              </Box>

              {/* アカウントセクション */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: AppColors.primary,
                    mb: 1,
                    px: 2,
                  }}
                >
                  アカウント
                </Typography>
                <Card sx={{ boxShadow: 2, mx: 2 }}>
                  <List sx={{ p: 0 }}>
                    <ListItemButton
                      onClick={() => setLogoutDialogOpen(true)}
                      sx={{
                        '&:hover': { bgcolor: '#f5f5f5' },
                      }}
                    >
                      <ListItemIcon>
                        <LogoutIcon sx={{ color: AppColors.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="ログアウト"
                        secondary="アカウントからログアウトします"
                        primaryTypographyProps={{
                          fontSize: 16,
                          fontWeight: 500,
                        }}
                        secondaryTypographyProps={{
                          fontSize: 14,
                          color: AppColors.textSecondary,
                        }}
                      />
                    </ListItemButton>
                  </List>
                </Card>
              </Box>
          </Box>
        </Box>

        {/* ログアウト確認ダイアログ */}
        <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)}>
          <DialogTitle>ログアウト</DialogTitle>
          <DialogContent>
            <DialogContentText>
              ログアウトしますか？
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLogoutDialogOpen(false)}>キャンセル</Button>
            <Button onClick={handleLogout} color="primary" variant="contained">ログアウト</Button>
          </DialogActions>
        </Dialog>

        {/* 匿名性についてダイアログ */}
        <Dialog open={anonymityDialogOpen} onClose={() => setAnonymityDialogOpen(false)}>
          <DialogTitle>匿名性について</DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <ShieldIcon sx={{ color: AppColors.primary, mr: 1.5, mt: 0.5 }} />
                <Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
                    完全匿名での投稿
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: AppColors.textSecondary, mb: 2 }}>
                    あなたの投稿は、管理者を含む誰にも個人が特定されることはありません。安心してご利用ください。
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <PrivacyTipIcon sx={{ color: AppColors.primary, mr: 1.5, mt: 0.5 }} />
                <Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
                    プライバシー保護
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: AppColors.textSecondary }}>
                    投稿内容は暗号化され、安全に保存されます。あなたの個人情報は一切収集されません。
                  </Typography>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAnonymityDialogOpen(false)}>閉じる</Button>
          </DialogActions>
        </Dialog>
      </ResponsiveAdminLayout>
    );
  }

  // 一般ユーザーの場合
  return (
    <ResponsiveUserLayout 
      maxWidth="1000px"
      topFullWidth={
        <>
          {/* モバイル専用ヘッダー（戻る＋設定） */}
          {!isDesktop && (
            <AppBar position="static" sx={{ bgcolor: 'white', boxShadow: 'none', borderBottom: '1px solid #e0e0e0' }}>
              <Toolbar>
                <IconButton edge="start" onClick={() => navigate(-1)} sx={{ color: 'black' }}>
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" sx={{ flexGrow: 1, color: 'black', fontWeight: 600 }}>設定</Typography>
              </Toolbar>
            </AppBar>
          )}
          {/* 中ヘッダー（PC表示用） */}
          {isDesktop && (
            <Box sx={{ width: '100%', p: 2, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'black' }}>
                設定
              </Typography>
            </Box>
          )}
        </>
      }
    >
      <Box sx={{ 
        bgcolor: AppColors.surface,
        pb: 2,
      }}>

        {/* ユーザー情報セクション（管理者のみ表示） */}
        {isAdmin && (
          <Box sx={{ mb: 2, mt: isDesktop ? 0 : 2 }}>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: AppColors.primary,
                mb: 1,
                px: 2,
              }}
            >
              ユーザー情報
            </Typography>
            <Card sx={{ boxShadow: 2, mx: 2 }}>
              <List sx={{ p: 0 }}>
                <ListItem>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: AppColors.primary }}>
                      <Typography sx={{ fontSize: 16, fontWeight: 'bold', color: 'white' }}>
                        {(user?.displayName || user?.email || 'U').charAt(0)}
                      </Typography>
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
                          {user?.displayName || 'ユーザー'}
                        </Typography>
                        {(user?.userType === 'superadmin' || user?.userType === 'admin') && (
                          <Chip
                            label={user?.userType === 'superadmin' ? 'システム管理者' : '管理者'}
                            size="small"
                            sx={{ height: 22, bgcolor: `${AppColors.primary}33`, color: AppColors.primary, fontWeight: 600 }}
                          />
                        )}
                      </Box>
                    }
                    secondary={user?.email || 'メール未設定'}
                    secondaryTypographyProps={{
                      fontSize: 14,
                      color: AppColors.textSecondary,
                    }}
                  />
                </ListItem>
              </List>
            </Card>
          </Box>
        )}

        {/* コンテンツエリア（ResponsiveUserLayoutが標準の余白を処理） */}
        <Box sx={{ mt: 2 }}>
        {/* プライバシーセクション（管理者・システム管理者には表示しない） */}
        {!isAdmin && (
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: AppColors.primary,
                mb: 1,
                px: 2,
              }}
            >
              プライバシー
            </Typography>
            <Card sx={{ boxShadow: 2, mx: 2 }}>
              <List sx={{ p: 0 }}>
                <ListItemButton
                  onClick={handleAnonymityInfo}
                  sx={{
                    '&:hover': { bgcolor: '#f5f5f5' },
                  }}
                >
                  <ListItemIcon>
                    <ShieldIcon sx={{ color: AppColors.primary }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="匿名性について"
                    secondary="あなたの投稿は完全に匿名です"
                    primaryTypographyProps={{
                      fontSize: 16,
                      fontWeight: 500,
                    }}
                    secondaryTypographyProps={{
                      fontSize: 14,
                      color: AppColors.textSecondary,
                    }}
                  />
                </ListItemButton>
              </List>
            </Card>
          </Box>
        )}

        {/* ヘルプセクション */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: AppColors.primary,
              mb: 1,
              px: 2,
            }}
          >
            ヘルプ
          </Typography>
          <Card sx={{ boxShadow: 2, mx: 2 }}>
            <List sx={{ p: 0 }}>
              <ListItemButton
                onClick={() => navigate('/help')}
                sx={{
                  '&:hover': { bgcolor: '#f5f5f5' },
                }}
              >
                <ListItemIcon>
                  <HelpIcon sx={{ color: AppColors.primary }} />
                </ListItemIcon>
                <ListItemText
                  primary="ヘルプ"
                  secondary="よくある質問とサポート"
                  primaryTypographyProps={{
                    fontSize: 16,
                    fontWeight: 500,
                  }}
                  secondaryTypographyProps={{
                    fontSize: 14,
                    color: AppColors.textSecondary,
                  }}
                />
              </ListItemButton>
              <ListItemButton
                onClick={() => navigate('/about-app')}
                sx={{
                  '&:hover': { bgcolor: '#f5f5f5' },
                }}
              >
                <ListItemIcon>
                  <InfoIcon sx={{ color: AppColors.primary }} />
                </ListItemIcon>
                <ListItemText
                  primary="アプリについて"
                  secondary="アプリの機能と使い方"
                  primaryTypographyProps={{
                    fontSize: 16,
                    fontWeight: 500,
                  }}
                  secondaryTypographyProps={{
                    fontSize: 14,
                    color: AppColors.textSecondary,
                  }}
                />
              </ListItemButton>
            </List>
          </Card>
        </Box>

        {isAdmin && (
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: AppColors.primary,
                mb: 1,
                px: 2,
              }}
            >
              アカウント
            </Typography>
            <Card sx={{ boxShadow: 2, mx: 2 }}>
              <List sx={{ p: 0 }}>
                <ListItemButton
                  onClick={() => setLogoutDialogOpen(true)}
                  sx={{
                    '&:hover': { bgcolor: '#f5f5f5' },
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon sx={{ color: AppColors.primary }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="ログアウト"
                    secondary="アカウントからログアウトします"
                    primaryTypographyProps={{
                      fontSize: 16,
                      fontWeight: 500,
                    }}
                    secondaryTypographyProps={{
                      fontSize: 14,
                      color: AppColors.textSecondary,
                    }}
                  />
                </ListItemButton>
              </List>
            </Card>
            </Box>
        )}
        </Box>
      </Box>

      {/* ログアウト確認ダイアログ */}
      <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)}>
        <DialogTitle>ログアウト</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ログアウトしますか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleLogout} color="primary" variant="contained">ログアウト</Button>
        </DialogActions>
      </Dialog>

      {/* 匿名性についてダイアログ */}
      <Dialog open={anonymityDialogOpen} onClose={() => setAnonymityDialogOpen(false)}>
        <DialogTitle>匿名性について</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <ShieldIcon sx={{ color: AppColors.primary, mr: 1.5, mt: 0.5 }} />
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
                  完全匿名での投稿
                </Typography>
                <Typography sx={{ fontSize: 14, color: AppColors.textSecondary, mb: 2 }}>
                  あなたの投稿は、管理者を含む誰にも個人が特定されることはありません。安心してご利用ください。
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <PrivacyTipIcon sx={{ color: AppColors.primary, mr: 1.5, mt: 0.5 }} />
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
                  プライバシー保護
                </Typography>
                <Typography sx={{ fontSize: 14, color: AppColors.textSecondary }}>
                  投稿内容は暗号化され、安全に保存されます。あなたの個人情報は一切収集されません。
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnonymityDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </ResponsiveUserLayout>
  );
};